import type { AppConfig } from "../../types/appConfig";
import type { Equipment } from "../equipment/Equipment.typed";
import type {
  Rental,
  RentalAddItemRequest,
  RentalCreateRequest,
  RentalItem,
  RentalListFilters,
  RentalModelPricing,
  RentalModelPricingUpdate,
  RentalProduct,
  RentalProductRequest,
  RentalUpdateRequest,
} from "./Rental.typed";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";

export class RentalModel {
  private base: string;

  constructor(config: AppConfig) {
    this.base = config.zekayAPI;
  }

  // -- Catalogue (Ventes) ---------------------------------------------------

  async listRentalProducts(): Promise<RentalProduct[]> {
    const res = await fetchZekay(`${this.base}/rental-products`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as RentalProduct[];
  }

  async getRentalProduct(id: number): Promise<RentalProduct> {
    const res = await fetchZekay(`${this.base}/rental-products/${id}`);
    await assertResponseOk(res);
    return (await res.json()) as RentalProduct;
  }

  async createRentalProduct(payload: RentalProductRequest): Promise<RentalProduct> {
    const res = await fetchZekay(`${this.base}/rental-products`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201);
    return (await res.json()) as RentalProduct;
  }

  async updateRentalProduct(id: number, payload: RentalProductRequest): Promise<RentalProduct> {
    const res = await fetchZekay(`${this.base}/rental-products/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(res);
    return (await res.json()) as RentalProduct;
  }

  async deleteRentalProduct(id: number): Promise<void> {
    const res = await fetchZekay(`${this.base}/rental-products/${id}`, { method: "DELETE" });
    await expectStatuses(res, 204);
  }

  // -- Rentals (Centre) -----------------------------------------------------

  async listRentals(filters?: RentalListFilters): Promise<Rental[]> {
    const params = new URLSearchParams();
    if (filters?.customer_id != null) params.set("customer_id", String(filters.customer_id));
    if (filters?.status) params.set("status", filters.status);
    const query = params.toString();
    const url = query ? `${this.base}/rentals?${query}` : `${this.base}/rentals`;
    const res = await fetchZekay(url);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];
    return (await res.json()) as Rental[];
  }

  async getRental(id: number): Promise<Rental> {
    const res = await fetchZekay(`${this.base}/rentals/${id}`);
    await assertResponseOk(res);
    return (await res.json()) as Rental;
  }

  async createRental(payload: RentalCreateRequest): Promise<Rental> {
    const res = await fetchZekay(`${this.base}/rentals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201);
    return (await res.json()) as Rental;
  }

  async updateRental(id: number, payload: RentalUpdateRequest): Promise<Rental> {
    const res = await fetchZekay(`${this.base}/rentals/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await assertResponseOk(res);
    return (await res.json()) as Rental;
  }

  async deleteRental(id: number): Promise<void> {
    const res = await fetchZekay(`${this.base}/rentals/${id}`, { method: "DELETE" });
    await expectStatuses(res, 204);
  }

  // -- Rental items ---------------------------------------------------------

  async addRentalItem(rentalID: number, payload: RentalAddItemRequest): Promise<RentalItem> {
    const res = await fetchZekay(`${this.base}/rentals/${rentalID}/items`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201);
    return (await res.json()) as RentalItem;
  }

  async returnRentalItem(
    rentalID: number,
    itemID: number,
    state: "returned" | "not_returned" = "returned",
  ): Promise<void> {
    const res = await fetchZekay(`${this.base}/rentals/${rentalID}/items/${itemID}/return`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state }),
    });
    await expectStatuses(res, 204);
  }

  async removeRentalItem(rentalID: number, itemID: number): Promise<void> {
    const res = await fetchZekay(`${this.base}/rentals/${rentalID}/items/${itemID}`, {
      method: "DELETE",
    });
    await expectStatuses(res, 204);
  }

  // -- Helpers --------------------------------------------------------------

  async listRentalModelPricing(): Promise<RentalModelPricing[]> {
    const res = await fetchZekay(`${this.base}/rental-equipment-pricing`);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];

    return (await res.json()) as RentalModelPricing[];
  }

  async updateRentalModelPricing(
    modelID: number,
    payload: RentalModelPricingUpdate,
  ): Promise<void> {
    const res = await fetchZekay(`${this.base}/rental-equipment-pricing/${modelID}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 204);
  }

  async listAvailableEquipments(modelID?: number, query?: string): Promise<Equipment[]> {
    const params = new URLSearchParams();
    if (modelID != null) params.set("model_id", String(modelID));
    const q = query?.trim();
    if (q) params.set("q", q);

    const suffix = params.toString();
    const url = suffix
      ? `${this.base}/rental-equipment/available?${suffix}`
      : `${this.base}/rental-equipment/available`;

    const res = await fetchZekay(url);
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return [];

    return (await res.json()) as Equipment[];
  }
}
